/*----------------------------------
- MODULE: PUBLIC PRODUCT READ CACHE
----------------------------------*/

/**
 * @docs docs/features/website-landing-experience
 * @fix  docs/fixes/2026-09-19-modal-close-header-navigation-crash.md
 * @rule A cached read is never served in the browser: `read()` returns null and `seed()` is a
 *       no-op there, so the client router always receives the untouched fetcher descriptor.
 * @rule Only visitor-independent public Product reads may be cached here. A payload
 *       that varies by cookie, session, or member must go through its accessor
 *       directly, never through one of these caches.
 */

// Every landing page's data fetcher needs the same header navigation, and the
// stats and public pricing payloads are equally identical for every visitor.
// Without this cache each SSR render paid one connected HTTP call to Product
// per read (26 header-navigation calls in one local trace window, up to 5.5s
// each while Product was busy), which throttled website TTFB for no freshness
// gain.
//
// Shape constraint: page `data` entries that are Proteum fetcher descriptors
// are resolved by the router's own connected machinery, so a cache must never
// adopt a descriptor with `Promise.resolve` (that bypasses the connected
// resolution and delivers the wrong shape). Instead `read()` serves only
// already-resolved values, `data` falls back to the untouched descriptor on a
// miss, and the surface where the resolved payload lands calls `seed(...)`.
// Sharing across requests is deliberate on the server: these payloads are
// public and identical for every visitor, so the long-lived server module
// caching across requests is the point, not a leak.
//
// The cache is server-only. The server router awaits every `data` entry, so a
// Promise is fine there, but the client router treats every entry as a fetcher
// descriptor and posts them all to `/api`, where a Promise serializes to `{}`.
// The first client-side transition (closing the conversion modal, for example)
// then rendered the Header with a navigation missing `tlds` and crashed on
// `.reduce`. In the browser the router batches descriptors itself, so the cache
// has nothing to save there.

import type { PublicRegistrarProfileResult } from '@uniquedomains/components/contracts/registrars/public';
import { toUtcDayKey } from '@uniquedomains/core/dates';

import type { ProductConnectedResult } from '@/common/contracts/connected/product';

const PUBLIC_PRODUCT_READ_TTL_MS = 10 * 60 * 1000;

/**
 * A payload's own freshness beside the window: a page drawn for one UTC day (a daily rotation) is
 * stale at midnight whatever is left of its window. A value it rejects is never served and never
 * seeded, and a stored entry it rejects counts as absent, so the next seed replaces it at once.
 */
type CachedPublicProductReadOptions<TPayload> = { isFresh?: (value: TPayload) => boolean };

type CachedPublicProductRead<TPayload> = {
    /** Fresh cached value as an awaitable data entry, or null so `data` uses the real accessor. */
    read: () => Promise<TPayload> | null;
    /** Store the resolved payload where it lands; no-op while the current window is still fresh. */
    seed: (value: TPayload | null | undefined) => void;
};

const isBrowser = (): boolean => typeof window !== 'undefined';

type CacheEntry<TPayload> = { expiresAtMs: number; value: TPayload };

/** An entry still inside its window and, when the cache has a freshness rule, still fresh by it. */
const isLiveEntry = <TPayload>(entry: CacheEntry<TPayload> | null | undefined, isFresh?: (value: TPayload) => boolean): entry is CacheEntry<TPayload> =>
    Boolean(entry && entry.expiresAtMs > Date.now() && (!isFresh || isFresh(entry.value)));

const createCachedPublicProductRead = <TPayload>({ isFresh }: CachedPublicProductReadOptions<TPayload> = {}): CachedPublicProductRead<TPayload> => {
    let entry: CacheEntry<TPayload> | null = null;
    return {
        read: () => {
            if (isBrowser()) return null;
            if (isLiveEntry(entry, isFresh)) return Promise.resolve(entry.value);
            return null;
        },
        seed: (value) => {
            if (isBrowser()) return;
            if (!value) return;
            if (isFresh && !isFresh(value)) return;
            // Fixed windows, not sliding: re-seeding on every render would let a
            // stale value survive forever under constant traffic. After expiry
            // the next `data` misses, fetches for real, and re-seeds fresh.
            if (isLiveEntry(entry, isFresh)) return;
            entry = { expiresAtMs: Date.now() + PUBLIC_PRODUCT_READ_TTL_MS, value };
        },
    };
};

export const cachedLandingHeaderNavigation =
    createCachedPublicProductRead<ProductConnectedResult<'Navigation.GetLandingHeaderNavigation'>>();

/**
 * The bare `/domains` public search cut to Home's ten rows (`previewRowLimit`), read by Home alone.
 *
 * `/domains` never reads or seeds it: its own resolution carries twenty rows and the filter
 * catalog, a different payload. Home seeds it only from a resolution it could render, so a pending
 * or empty read is retried on the next render instead of hiding the preview for a whole window.
 */
export const cachedLandingDomainsPreview =
    createCachedPublicProductRead<ProductConnectedResult<'Domains.search.ResolvePublicSearch'>>();

export const cachedLandingStats =
    createCachedPublicProductRead<ProductConnectedResult<'Stats.general'>>();

export const cachedPublicPricingPlans =
    createCachedPublicProductRead<ProductConnectedResult<'Plans.getPublicPricingPlans'>>();

/**
 * The public sales page (ADR-0067). A `null` payload (before the first nightly rollup) is never
 * seeded, so the page retries on the next render instead of caching the pending state.
 */
export const cachedPublicSalesPage =
    createCachedPublicProductRead<NonNullable<ProductConnectedResult<'Sales.GetPublicPage'>>>();

/**
 * The public auctions page (ADR-0076). A `null` payload (no sample yet) is never seeded, and a page
 * of another UTC day is not seeded either, so the new day's sample shows on the next render.
 */
export const cachedPublicAuctionsPage =
    createCachedPublicProductRead<NonNullable<ProductConnectedResult<'Auctions.GetPublicPage'>>>();

/**
 * The two lookup hubs (ADR-0078): the same dated counts for every visitor, so the hub page and
 * every result page share one read per window. Never a lookup answer: those are per visitor and
 * go through the gateway, uncached.
 */
export const cachedPublicWhoisHub =
    createCachedPublicProductRead<ProductConnectedResult<'Lookup.Public.WhoisHub'>>();

export const cachedPublicAvailabilityHub =
    createCachedPublicProductRead<ProductConnectedResult<'Lookup.Public.AvailabilityHub'>>();

/** True when a registrar page's day (`YYYY-MM-DD`) is today's UTC day. */
const isTodayUtc = (day: string | null): boolean => day !== null && day === toUtcDayKey(new Date());

/**
 * The public registrar hub (ADR-0079). A `null` payload (no price list read yet) is never seeded,
 * and a hub drawn for another UTC day (`rotationDay`) is neither seeded nor served: an entry
 * seeded at 23:55 stops answering at midnight, so yesterday's rotation never outlives its day.
 */
export const cachedPublicRegistrarsHub =
    createCachedPublicProductRead<NonNullable<ProductConnectedResult<'Registrars.GetPublicHub'>>>({ isFresh: (hub) => isTodayUtc(hub.rotationDay) });

/** At most this many profiles stay in memory; the oldest entry goes first. */
const PUBLIC_PRODUCT_READ_KEYED_MAX = 64;

type CachedPublicProductReadByKey<TPayload> = {
    read: (key: string) => Promise<TPayload> | null;
    seed: (key: string, value: TPayload | null | undefined) => void;
};

/**
 * A keyed cache with the same rules as `createCachedPublicProductRead` (server only, fixed windows,
 * never a null, the optional freshness rule), one window per key, bounded so a crawl of every key
 * cannot grow it without end.
 */
const createCachedPublicProductReadByKey = <TPayload>({ isFresh }: CachedPublicProductReadOptions<TPayload> = {}): CachedPublicProductReadByKey<TPayload> => {
    const entries = new Map<string, CacheEntry<TPayload>>();
    return {
        read: (key) => {
            if (isBrowser()) return null;
            const entry = entries.get(key);
            if (isLiveEntry(entry, isFresh)) return Promise.resolve(entry.value);
            return null;
        },
        seed: (key, value) => {
            if (isBrowser() || !value) return;
            if (isFresh && !isFresh(value)) return;
            if (isLiveEntry(entries.get(key), isFresh)) return;
            entries.delete(key);
            entries.set(key, { expiresAtMs: Date.now() + PUBLIC_PRODUCT_READ_TTL_MS, value });
            while (entries.size > PUBLIC_PRODUCT_READ_KEYED_MAX) {
                const oldest = entries.keys().next().value;
                if (oldest === undefined) break;
                entries.delete(oldest);
            }
        },
    };
};

/** The UTC day a profile answer is drawn for: a registrar's rotation, a venue's footprint; null otherwise. */
const registrarProfileDay = (result: PublicRegistrarProfileResult): string | null => {
    if (result.status === 'registrar') return result.profile.rotationDay;
    if (result.status === 'venue') return result.profile.footprintDay;
    return null;
};

/**
 * One public registrar or venue profile per slug (ADR-0079). Only a registrar or venue answer of
 * today's UTC day is seeded or served (a registrar's `rotationDay`, a venue's `footprintDay`):
 * never `not_found` or `unavailable` (they carry no day), and never yesterday's after midnight.
 */
export const cachedPublicRegistrarProfiles =
    createCachedPublicProductReadByKey<ProductConnectedResult<'Registrars.GetPublicProfile'>>({ isFresh: (result) => isTodayUtc(registrarProfileDay(result)) });
